/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * PageShortcutsContext — one keyboard-shortcuts help surface for the whole app.
 *
 * The global "?" help modal (GlobalShortcutHelp in SidebarLayout) shows the
 * app-wide shortcuts. A page with shortcuts of its own (e.g. the Class
 * Schedule tab) publishes them through this context while it is mounted and
 * enabled; the modal then shows the page's shortcuts merged with the global
 * ones, and the publication is withdrawn automatically on unmount. This
 * replaces the previous split where such a page owned a second, separate
 * shortcuts modal and the global one had to defer to it by route.
 */

import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardShortcut } from "../hooks/useKeyboardShortcuts";

interface PageShortcutsContextValue {
  /** Shortcuts published by the currently mounted page, or null when the
   * active page has none. */
  pageShortcuts: KeyboardShortcut[] | null;
  setPageShortcuts: (shortcuts: KeyboardShortcut[] | null) => void;
}

const PageShortcutsContext = createContext<PageShortcutsContextValue>({
  pageShortcuts: null,
  setPageShortcuts: () => {},
});

export function PageShortcutsProvider({ children }: { children: React.ReactNode }) {
  const [pageShortcuts, setPageShortcuts] = useState<KeyboardShortcut[] | null>(null);
  const value = useMemo(() => ({ pageShortcuts, setPageShortcuts }), [pageShortcuts]);
  return <PageShortcutsContext.Provider value={value}>{children}</PageShortcutsContext.Provider>;
}

export function usePageShortcuts(): PageShortcutsContextValue {
  return useContext(PageShortcutsContext);
}

/**
 * Publish a page's shortcuts to the global help modal while the caller is
 * mounted and `enabled`. Keyed on a content signature (keys + descriptions),
 * not array identity, so pages that rebuild the array every render don't
 * re-publish in a loop.
 */
export function usePublishPageShortcuts(shortcuts: KeyboardShortcut[], enabled: boolean): void {
  const { setPageShortcuts } = usePageShortcuts();

  // Same ref pattern as useKeyboardShortcuts: this effect runs before the
  // publish effect below (declaration order), so the ref is fresh there.
  const shortcutsRef = useRef(shortcuts);
  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  const signature = shortcuts
    .map((s) => `${s.ctrlKey ? "^" : ""}${s.shiftKey ? "+" : ""}${s.key}:${s.description}`)
    .join("|");

  useEffect(() => {
    if (!enabled) return undefined;
    setPageShortcuts(shortcutsRef.current);
    return () => setPageShortcuts(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- signature stands in for shortcuts (content identity, not array identity)
  }, [enabled, signature, setPageShortcuts]);
}
